import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Calendar } from "./Calendar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  AlertCircle,
  Check,
  User,
  Users,
  Calendar as CalendarIcon,
} from "lucide-react";
import ServiceSelection from "./ServiceSelection";
import { Service, SelectedOptionsType } from "@/utils/types";
import { format } from "date-fns";
import { toast } from "sonner";

export type Employee = {
  id: number;
  name: string;
  availability_hours: {
    [key: string]: Array<{
      start_time: string;
      end_time: string;
    }>;
  };
};

export interface SelectedService extends Service {
  quantity: number;
  selected_options: SelectedOptionsType;
  uniqueId: string;
  appointment_duration?: number;
  selected_appointment_date_time?: Date;
}

type Customer = {
  name: string;
  phone: string;
};

type EmployeeAvailabilityHours = {
  [key: string]: Array<{
    start_time: string;
    end_time: string;
  }>;
};

type CreateOrderPayload = {
  branch: number;
  order_items: Array<{
    product: number;
    quantity: number;
    price_set: number;
    discount_set: number;
    branch: number;
  }>;
  payment_status: string;
  tip: number;
  is_delivery: boolean;
  customer_name: string;
  phone_number: string;
  pickup_time?: string;
  status: string;
  order_type: string;
  sales_channel: string;
  subtotal_price: number;
  total_price: number;
  tax: number;
};

type CreateAppointmentPayload = {
  branch: number;
  appointment_time: Date;
  duration: number;
  employee: number;
  order: number;
  product: number;
  status: string;
  customer_name: string;
  phone_number: string;
};

const CreateAppointmentForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showServiceSelection, setShowServiceSelection] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );
  const [selectedService, setSelectedService] =
    useState<SelectedService | null>(null);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null);
  const [branch, setBranch] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<Customer>({
    name: "",
    phone: "",
  });

  const [serviceProviders, setServiceProviders] = useState<Employee[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [employeeAvailabilityHours, setEmployeeAvailabilityHours] =
    useState<EmployeeAvailabilityHours | null>(null);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);

  useEffect(() => {
    const storedData = localStorage.getItem("user_data");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      const { selectedBranch } = parsedData;
      setBranch(selectedBranch.id);
    }
  }, []);

  useEffect(() => {
    if (selectedService) {
      fetchEmployeesThatProvideThisService(selectedService.id);
    } else {
      setServiceProviders([]);
      setSelectedEmployee(null);
    }
  }, [selectedService]);

  useEffect(() => {
    if (selectedEmployee) {
      fetchEmployeeAvailabilityHours(selectedEmployee.id);
    } else {
      setEmployeeAvailabilityHours(null);
    }
  }, [selectedEmployee]);

  const fetchEmployeeAvailabilityHours = async (employeeId: number) => {
    setIsLoadingAvailability(true);
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/employee-availability/`,
        {
          params: {
            employee_id: employeeId,
          },
        },
      );
      setEmployeeAvailabilityHours(response.data);
    } catch (err) {
      console.error("Error fetching employee availability:", err);
      setEmployeeAvailabilityHours(null);
    } finally {
      setIsLoadingAvailability(false);
    }
  };

  const fetchEmployeesThatProvideThisService = async (serviceId: number) => {
    setIsLoadingProviders(true);
    setFetchError(null);
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/employee/employees_that_provide_this_service/?service_id=${serviceId}`,
      );

      const providersData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];

      setServiceProviders(providersData);

      setSelectedEmployee(null);
      setEmployeeAvailabilityHours(null);
    } catch (err) {
      console.error("Error fetching service providers:", err);
      setFetchError("Failed to load service providers");
      setServiceProviders([]);
    } finally {
      setIsLoadingProviders(false);
    }
  };

  const handleProviderSelection = (value: string) => {
    if (value === "") {
      setSelectedEmployee(null);
      setEmployeeAvailabilityHours(null);
    } else if (value === "random" && serviceProviders.length > 0) {
      const randomIndex = Math.floor(Math.random() * serviceProviders.length);
      const employee = serviceProviders[randomIndex];
      setSelectedEmployee(employee);
    } else {
      const selected = serviceProviders.find(
        (provider) => provider.id === Number(value),
      );
      setSelectedEmployee(selected || null);
    }

    setSelectedDateTime(null);
  };

  const createPostPaidOrder = async (): Promise<number | null> => {
    if (!selectedService || !branch) {
      toast.error("Missing service or branch information");
      return null;
    }

    try {
      const productPrice = selectedService.price || 0;

      const requestBody: CreateOrderPayload = {
        branch: branch,
        order_items: [
          {
            product: selectedService.id,
            quantity: 1,
            price_set: productPrice,
            discount_set: 0,
            branch: branch,
          },
        ],
        payment_status: "PostPaid",
        tip: 0,
        is_delivery: false,
        customer_name: customers.name,
        phone_number: `+1${customers.phone.replace(/\D/g, "")}`,
        status: "Created",
        order_type: "Appointment",
        sales_channel: "Online",
        tax: 0,
        subtotal_price: productPrice,
        total_price: productPrice,
      };

      const response = await axios.post(
        `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/order/`,
        requestBody,
      );

      return response.data.id;
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Failed to create order");
      return null;
    }
  };

  const createAppointment = async (orderId: number): Promise<boolean> => {
    if (!selectedDateTime || !selectedEmployee || !selectedService || !branch) {
      toast.error("Missing appointment information");
      return false;
    }

    try {
      const appointmentTimeISO = selectedDateTime.toISOString();

      console.log("Sending appointment time:", appointmentTimeISO);

      const requestBody: CreateAppointmentPayload = {
        branch: branch,
        appointment_time: selectedDateTime,
        duration: selectedService.duration || 30,
        employee: selectedEmployee.id,
        order: orderId,
        product: selectedService.id,
        status: "Scheduled",
        customer_name: customers.name,
        phone_number: `+1${customers.phone.replace(/\D/g, "")}`,
      };

      await axios.post(
        `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/appointment/`,
        {
          ...requestBody,
          appointment_time: appointmentTimeISO,
        },
      );

      return true;
    } catch (error) {
      console.error("Error creating appointment:", error);
      toast.error("Failed to create appointment");
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !selectedEmployee ||
      !selectedService ||
      !selectedDateTime ||
      !customers.name ||
      !customers.phone
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const orderId = await createPostPaidOrder();

      if (!orderId) {
        setError("Failed to create order");
        setLoading(false);
        return;
      }

      const appointmentCreated = await createAppointment(orderId);

      if (appointmentCreated) {
        toast.success("Appointment created successfully");
        navigate("/appointments/");
      } else {
        setError("Failed to create appointment");
      }
    } catch (error) {
      console.error("Error in appointment creation process:", error);
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (showServiceSelection) {
    return (
      <ServiceSelection
        onClose={() => setShowServiceSelection(false)}
        branch={branch}
        setShowServiceSelection={setShowServiceSelection}
        setSelectedService={setSelectedService}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <button
        onClick={() => navigate("/appointments")}
        className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-800"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        {t("back.management.appointment.back")}
      </button>

      <h1 className="mb-6 text-2xl font-bold">
        {t("back.management.appointment.create")}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div className="space-y-6">
            {/* Service Selection */}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 text-lg font-medium text-gray-800">
                {t("back.management.appointment.service")}
              </h3>
              <div className="mb-4 flex items-center">
                {selectedService ? (
                  <div className="flex w-full items-center justify-between rounded-md bg-gray-50 p-3">
                    <div className="flex items-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-500">
                        <Check className="h-5 w-5" />
                      </div>
                      <span className="ml-3 font-medium text-gray-800">
                        {selectedService.name}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                      onClick={() => setShowServiceSelection(true)}
                    >
                      {t("back.management.appointment.change_service")}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full border-red-500 px-6 py-6 text-red-500 hover:bg-red-500 hover:text-white"
                    onClick={() => setShowServiceSelection(true)}
                  >
                    <span className="text-lg">
                      {t("back.management.appointment.select_service")}
                    </span>
                  </Button>
                )}
              </div>
            </div>

            {/* Employee Selection */}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 text-lg font-medium text-gray-800">
                {t("back.management.appointment.employee")}
              </h3>

              {!selectedService ? (
                <div className="flex items-center rounded-md bg-gray-50 p-4 text-gray-500">
                  <AlertCircle className="mr-2 h-5 w-5 text-red-400" />
                  <span className="text-sm">
                    {t("back.management.appointment.please_select_service")}
                  </span>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedEmployee && (
                    <div className="flex items-center rounded-md bg-gray-50 p-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-500">
                        <User className="h-5 w-5" />
                      </div>
                      <span className="ml-3 font-medium text-gray-800">
                        {selectedEmployee.name}
                      </span>
                    </div>
                  )}

                  {isLoadingProviders ? (
                    <div className="flex items-center rounded-md bg-blue-50 p-4 text-blue-600">
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      <span className="text-sm">
                        Loading service providers...
                      </span>
                    </div>
                  ) : fetchError ? (
                    <div className="flex items-center rounded-md bg-red-50 p-4 text-red-600">
                      <AlertCircle className="mr-2 h-5 w-5" />
                      <span className="text-sm">{fetchError}</span>
                    </div>
                  ) : serviceProviders.length === 0 ? (
                    <div className="flex items-center rounded-md bg-amber-50 p-4 text-amber-600">
                      <AlertCircle className="mr-2 h-5 w-5" />
                      <span className="text-sm">
                        No service providers available for this service.
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-500">
                          {serviceProviders.length} providers available
                        </span>
                      </div>
                      <select
                        id="serviceProvider"
                        className="w-full rounded-md border border-gray-300 px-4 py-3 text-gray-700 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                        value={
                          selectedEmployee ? selectedEmployee.id.toString() : ""
                        }
                        onChange={(e) =>
                          handleProviderSelection(e.target.value)
                        }
                      >
                        <option value="">Select a service provider</option>
                        {serviceProviders.map((provider) => (
                          <option
                            key={provider.id}
                            value={provider.id.toString()}
                          >
                            {provider.name}
                          </option>
                        ))}
                        <option value="random">Any Provider</option>
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Customer Information */}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 text-lg font-medium text-gray-800">
                Customer Information
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t("back.management.appointment.customer")}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={customers.name}
                    required
                    onChange={(e) =>
                      setCustomers({ ...customers, name: e.target.value })
                    }
                    className="w-full rounded-md border border-gray-300 p-3 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                    placeholder="Customer name"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t("back.management.appointment.phone")}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={customers.phone}
                    required
                    onChange={(e) =>
                      setCustomers({ ...customers, phone: e.target.value })
                    }
                    className="w-full rounded-md border border-gray-300 p-3 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                    placeholder="Phone number"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Calendar */}
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-800">
                {t("back.management.appointment.select_date_time")}
              </h3>
              {isLoadingAvailability && (
                <div className="flex items-center space-x-2 text-blue-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-xs">Loading availability...</span>
                </div>
              )}
            </div>

            {!selectedEmployee ? (
              <div className="flex h-48 flex-col items-center justify-center space-y-3 rounded-lg bg-gray-50 p-6 text-center">
                <CalendarIcon className="h-12 w-12 text-gray-300" />
                <p className="text-sm text-gray-500">
                  Select a service provider to view their availability
                </p>
              </div>
            ) : (
              <Calendar
                employeeAvailabilityHours={employeeAvailabilityHours}
                appointmentDuration={selectedService?.duration || 30}
                setSelectedDateTime={setSelectedDateTime}
                selectedEmployee={selectedEmployee}
              />
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end pt-6">
          <button
            type="submit"
            disabled={
              loading ||
              !selectedEmployee ||
              !selectedService ||
              !selectedDateTime ||
              !customers.name ||
              !customers.phone
            }
            className={`rounded-md bg-red-500 px-8 py-3 text-base font-medium text-white shadow-sm transition-colors ${
              loading ||
              !selectedEmployee ||
              !selectedService ||
              !selectedDateTime ||
              !customers.name ||
              !customers.phone
                ? "cursor-not-allowed opacity-50"
                : "hover:bg-red-600"
            } `}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span>{t("back.management.appointment.creating")}</span>
              </div>
            ) : (
              t("back.management.appointment.create")
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAppointmentForm;
